import { useEffect, useRef } from "react";

/**
 * useSafeSetTimeout
 *
 * A hook that returns a safe version of `setTimeout` that is cancelled if the
 * component is unmounted before the timeout finishes.
 *
 * @param callback The function to call after the timeout
 * @param delay The delay in milliseconds
 * @returns A function that can be called to clear the timeout
 */
export const useSafeSetTimeout = () => {
  const timeouts = useRef<ReturnType<typeof setTimeout>[]>([]);

  const _setTimeout = (callback: () => void, delay: number) => {
    const timeout = setTimeout(callback, delay);
    timeouts.current.push(timeout);
    return () => clearTimeout(timeout);
  };

  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      timeouts.current.forEach(clearTimeout);
    };
  }, []);

  return _setTimeout;
};
